import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { colors, fonts, glyphs, useMediaQuery } from "../../../Styles";
import { Button, CheckBox, NewInput, NewInputList, NewSelect, NewSelectList } from "../../../Components"
import { ButtonTypes, ListStyle_Types, SelectFieldOption_Values } from "../../../Definitions";
import { DirectionOptions, NumberingOptions, PatternOptions, Pen, Section, StartCornerOptions } from "./_Modals/newSectionModalInterfaces";
import { debounce } from "lodash";

// TODO
import getBorderRadius from "../../_Components/getBorderRadius";

// #region | generateNumberedGrid
function generateNumberedGrid( rows: number, columns: number, startCorner: StartCornerOptions, direction: DirectionOptions, pattern: PatternOptions, startingNumber: number ): Pen[] {
  const pens: Pen[] = [];
  const rowIndices = Array.from({ length: rows }, (_, i) => i);
  const colIndices = Array.from({ length: columns }, (_, i) => i);
  const rowOrder = startCorner.includes("bottom") ? rowIndices.reverse() : rowIndices;
  const colOrder = startCorner.includes("right") ? colIndices.reverse() : colIndices;
  
  let number = startingNumber;
  let coords: [number, number][] = [];

  if (direction === DirectionOptions.horizontal) {
    if (pattern === PatternOptions.z) {
      for (const r of rowOrder) {
        for (const c of colOrder) { coords.push([r, c]) }
      }
    } else if (pattern === PatternOptions.u) {
      let forward = true;
      for (const r of rowOrder) {
        if (forward) {
          for (const c of colOrder) { coords.push([r, c]) }
        } else {
          for (const c of colOrder.slice().reverse()) { coords.push([r, c]) }
        }
        forward = !forward;
      }
    }
  } else {
    if (pattern === PatternOptions.z) {
      for (const c of colOrder) {
        for (const r of rowOrder) { coords.push([r, c]) }
      }
    } else if (pattern === PatternOptions.u) {
      let forward = true;
      for (const c of colOrder) {
        if (forward) {
          for (const r of rowOrder) { coords.push([r, c]) }
        } else {
          for (const r of rowOrder.slice().reverse()) { coords.push([r, c]) }
        }
        forward = !forward;
      }
    }
  }
  for (const [r, c] of coords) {
    const penId = `${r * columns + c}`;
    const visual = `${r + 1}/${c + 1}/${r + 2}/${c + 2}`;
    pens.push({ penId, name: String(number++), visual, id: "", io: null });
  }

  return pens;
}
// #endregion

interface NewSectionModalLvl1_Props {
  section: Section;
  onSectionChange: (updatedSection: Section) => void;
}

interface CheckedContent {
  feederOnly: boolean,
  water: boolean,
  activator: boolean,
}

const NewSectionModalLvl1: React.FC<NewSectionModalLvl1_Props> = ({ section, onSectionChange }) => {
  const { t } = useTranslation()
  const mobileXs = useMediaQuery(`(max-width: 805px)`);
  const [name, setName] = useState<string>()
  const [rows, setRows] = useState<number>(2);
  const [columns, setColumns] = useState<number>(6);
  const [startingNumber, setStartingNumber] = useState<number>(101);
  const [numbering, setNumbering] = useState<NumberingOptions>(NumberingOptions.manual)
  const [startCorner, setStartCorner] = useState<StartCornerOptions>(StartCornerOptions.topLeft);
  const [direction, setDirection] = useState<DirectionOptions>(DirectionOptions.horizontal);
  const [pattern, setPattern] = useState<PatternOptions>(PatternOptions.z);
  const [checked, setChecked] = useState<CheckedContent>({feederOnly: true, water: false, activator: false})
  const [pens, setPens] = useState<Pen[]>([]);

  // Number of active pens
  const activePensCount = pens.filter((pen) => pen.penId !== null).length;

  // Load data if sectiondata already excists
  useEffect(() => {
    if (section) {
      setName(section.name ?? "");
      setColumns(section.columns ?? 2);
      setRows(section.rows ?? 6);
      setStartingNumber(Number(section.pens?.[0]?.name ?? 101));
      setChecked({
        feederOnly: section.feederOnly ?? true,
        water: section.water ?? false,
        activator: section.activator ?? false,
      });
      setPens(section.pens ?? []);
      setNumbering(section.numbering ?? NumberingOptions.manual)
      setStartCorner(section.startCorner ?? StartCornerOptions.topLeft)
      setDirection(section.direction ?? DirectionOptions.horizontal)
      setPattern(section.pattern ?? PatternOptions.z)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle pen update when numbering is automatic
  useEffect(() => {
    if (numbering === NumberingOptions.automatic && startCorner && direction && pattern) {
      const autoPens = generateNumberedGrid(
        rows,
        columns,
        startCorner,
        direction,
        pattern,
        startingNumber
      );
      setPens(autoPens);
    }
  }, [numbering, rows, columns, startCorner, direction, pattern, startingNumber]);

  // Update the section data when checkboxes are changed
  useEffect(() => {
    if (pens.length > 0) {
      const updatedSection: Section = { pens, columns, rows, lanController: "AICO_LAN1", feederOnly: checked.feederOnly, water: checked.water, activator: checked.activator, name, numbering, startCorner, direction, pattern};
      onSectionChange(updatedSection);
    }
  }, [checked, columns, rows, name, onSectionChange, pens, numbering, startCorner, direction, pattern]);  

  // #region | Handle Input Fields
  const handleRowsChange = (e: any) => {
    setRows(Number(e));
    setPens([]);
  };

  const handleColsChange = (e: any) => {
    setColumns(Number(e));
    setPens([]);
  };

  const handleStartNumberChange = (e: any) => {
    setStartingNumber(Number(e));
  };
  // #endregion

  // #region | Select Field Options
  const numberingOptions = Object.values(NumberingOptions).map((label, index) => ({
    id: index,
    label,
  }));

  const startCornerOptions = Object.values(StartCornerOptions).map((label, index) => ({
    id: index,
    label,
  }));
  const directionOptions = Object.values(DirectionOptions).map((label, index) => ({
    id: index,
    label,
  }))
  const patternOptions = Object.values(PatternOptions).map((label, index) => ({
    id: index,
    label,
  }))
  // #endregion

  // #region | Tableview functionality
  const totalCells = rows * columns;

  const handleCellClick = (penId: number) => {
    const penIdStr = String(penId);
    const existingIndex = pens.findIndex(p => p.penId === penIdStr);
  
    if (existingIndex !== -1) {
      const penToRemove = pens[existingIndex];
      const removedNameNum = parseInt(penToRemove.name ?? "", 10);
  
      let updated = pens.filter(p => p.penId !== penIdStr);
  
      updated = updated.map(pen => {
        const nameNum = parseInt(pen.name ?? "", 10);
        if (nameNum > removedNameNum) {
          return { ...pen, name: String(nameNum - 1) };
        }
        return pen;
      });
  
      setPens(updated);
  
      setNumbering(NumberingOptions.manual); // If a pen i clicked while numbering is automatic, numbering will update to manual
  
    } else {
      const existingNames = pens.map(p => parseInt(p.name ?? "", 10)).filter(n => !isNaN(n));
      const maxName = existingNames.length ? Math.max(...existingNames) : startingNumber - 1;
      const newName = maxName + 1;
  
      const rowStart = Math.floor(penId / columns) + 1;
      const colStart = (penId % columns) + 1;
      const rowEnd = rowStart + 1;
      const colEnd = colStart + 1;
      const visual = `${rowStart}/${colStart}/${rowEnd}/${colEnd}`;
  
      const newPen: Pen = { penId: penIdStr, visual, io: null, id: "", name: String(newName) };
  
      setPens([...pens, newPen]);
    }
  };
  

  const getCellNumber = (penId: number) => {
    const index = pens.findIndex(p => p.penId === String(penId));
    return index !== -1 ? String(startingNumber + index) : null;
  };
  // #endregion

  // #region | TableView
  const tableView = (
    <div style={{
      display: "grid",
      gridTemplateRows: `repeat(${rows}, 30px)`,
      gridTemplateColumns: columns > 2 ? `repeat(${columns}, 50px)` : `repeat(${columns}, 80px)`,
      gap: "10px",
    }}>
      {Array.from({ length: totalCells }).map((_, i) => {
        const isSelected = pens.some(pen => pen.penId === String(i));
        const number = getCellNumber(i);

        const row = Math.floor(i / columns);
        const col = i % columns;
        const borderRadius = getBorderRadius({ row, col, columns, rows });

        return (
          <div
            key={i}
            onClick={() => handleCellClick(i)}
            style={{
              textAlign: "center",
              alignContent: "center",
              backgroundColor: colors.greyLightSecondary,
              filter: isSelected ? "opacity(1)" : "opacity(0.5)",
              color: colors.greyDarkPrimary,
              ...fonts.paragraph,
              ...fonts.fontSize4,
              cursor: "pointer",
              borderRadius
            }}
          >
            {number ?? ""}
          </div>
        );
      })}
    </div>
  );
  // #endregion

  // #region | Handle Checkboxes
  const handleCheckboxChange = (name: keyof CheckedContent) => {
    setChecked((prevState) => {
      if (name === 'feederOnly') {
        return {
          feederOnly: true,
          water: false,
          activator: false,
        };
      } else {
        return {
          ...prevState,
          [name]: !prevState[name],
          feederOnly: false,
        };
      }
    });
  };


  // #endregion
  
  return (
    <div style={{ width: "100%", borderTop: "2px solid " + colors.blueDarkSecondary, margin: "20px 0px" }}>
      
      <div style={{marginBottom: mobileXs ? "20px" : undefined, padding: "10px 10px", maxHeight: "40vh", overflowY: "auto", overflowX: "hidden", scrollbarWidth: "thin", scrollbarColor: colors.blueDarkSecondary+colors.transparent, display: "grid", gridTemplateColumns: mobileXs ? "1fr" : "350px 1fr", gridTemplateRows: mobileXs ? "250px 1fr" : "1fr" }}>
        <div style={{ gridArea: "1/1/2/2", display: "flex", flexDirection: "column", gap: "15px", alignItems: mobileXs ? "center" : undefined }}>
          {/* Section Name */}
          <NewInput placeholder="" header={t("locationFeederPages.sections.sectionName", { defaultValue: "Section Name" })} isRequired onChange={debounce(setName, 300)} />
          {/* Columns, Rows, First Pen No. */}
          <NewInputList
            placeholders={["number", "number", "number"]}
            headers={[t("devices.columns", {defaultValue: "Columns"}), t("devices.rows", {defaultValue: "Rows"}), t("locationFeederPages.sections.firstPenNo", {defaultValue: "First Pen No."})]}
            onChanges={[handleColsChange, handleRowsChange, handleStartNumberChange]}
            valueOverrides={{
              defaultValues: [columns, rows, startingNumber],
              valueTypes: ["number", "number", "number"],
              maxValueNumbers: [4, undefined, undefined],
              minValueNumbers: [1, 1, 1]
            }}
            listOverrides={{
              listStyle: ListStyle_Types.vertical,
              listGap: "15px"
            }}
          />
          <NewSelect
            header="Numbering"
            options={numberingOptions}
            valueOverrides={{ defaultValue: numberingOptions.find(opt => opt.label === numbering) }}
            onChange={(option: SelectFieldOption_Values | undefined) => {
              if (option && typeof option.id === "number") {
                setNumbering(option.label as NumberingOptions);
              }
              setPens([]);
              onSectionChange({ ...section, pens: [] })
            }}
          />
          {(numbering === NumberingOptions.automatic) ? (<>
            <NewSelectList
              placeholders={["", "", ""]}
              headers={["start corner", "direction", "pattern"]}
              options={[startCornerOptions, directionOptions, patternOptions]} 
              listOverrides={{
                listStyle: ListStyle_Types.vertical,
                listGap: "10px"
              }}
              onChanges={[
                (option: SelectFieldOption_Values | undefined) => {
                  if (option && typeof option.id === "number") {
                    setStartCorner(option.label as StartCornerOptions);
                  }
                },
                (option: SelectFieldOption_Values | undefined) => {
                  if (option && typeof option.id === "number") {
                    setDirection(option.label as DirectionOptions);
                  }
                },
                (option: SelectFieldOption_Values | undefined) => {
                  if (option && typeof option.id === "number") {
                    setPattern(option.label as PatternOptions);
                  }
                }
              ]}
              valueOverrides={{
                defaultValues: [
                  startCornerOptions.find(opt => opt.label === startCorner) ?? { id: -1, label: "" },
                  directionOptions.find(opt => opt.label === direction) ?? { id: -1, label: "" },
                  patternOptions.find(opt => opt.label === pattern) ?? { id: -1, label: "" }
                ]
              }}
            />
          </> ) : ( <></> )}
        </div>
        {/* Table View */}
        <div style={{ gridArea: mobileXs ? "2/1/3/2" : "1/2/2/3", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "start", gap: "5px"}}>
          <div style={{display: "flex", alignItems: "center", justifyContent: "center", gap: "5px"}}>         
            <p style={{...fonts.header, ...fonts.fontSize4, color: colors.greyLightTertiary}}>{`${t("locationFeederPages.sections.penAmount", {defaultValue: "Number of Pens"})}:`}</p>
            <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyLightTertiary }}>{activePensCount}</p>
          </div>
          {tableView}
          <p style={{...fonts.paragraph, ...fonts.fontSize4, color: colors.greyLightTertiary, textAlign: "center", whiteSpace: 'pre-wrap', maxWidth: "150px"}}>{t("locationFeederPages.sections.clickOnPen", {defaultValue: "Click on a pen to remove it or add it again."})}</p>
        </div>

        {/* Reset Btn */}
        <div style={{position: "absolute", top: "185px", right: "45px"}}>
          <Button
            content={`${t("reset", {defaultValue: "reset"})}`}
            buttonType={ButtonTypes.textButton}
            endEnhancer={glyphs.ArrowReset({ size: "15px"})}
            onClick={() => {setPens([]); onSectionChange({ ...section, pens: [] })}}
          />
        </div> 
      </div>

      {/* Pen Content */}
      <p style={{ ...fonts.header, ...fonts.textShadow, ...fonts.fontSize4, color: colors.greyLightTertiary, padding: "0px 0px 2px 22px" }}>{t("locationFeederPages.sections.penContent", {defaultValue: "Pen Content"})}</p>
      <div style={{borderTop: "2px solid "+colors.blueDarkSecondary, borderBottom: "2px solid "+colors.blueDarkSecondary, padding: "10px 0px", maxHeight: "200px", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{width: "fit-content", display: "grid", gridTemplateColumns: mobileXs ? "1fr" : "1fr 1fr 1fr", gridTemplateRows: mobileXs ? "1fr 1fr 1fr" : "1fr", columnGap: "100px", rowGap: "10px"}}>
          <CheckBox text={t("locationFeederPages.sections.feederOnly", {defaultValue: "Feeder Only"})} isChecked={checked.feederOnly} onChange={() => handleCheckboxChange("feederOnly")}/> 
          <CheckBox text={t("locationFeederPages.sections.water", {defaultValue: "Water"})} isChecked={checked.water} onChange={() => handleCheckboxChange("water")}/> 
          <CheckBox text={t("locationFeederPages.sections.activator", {defaultValue: "Activator"})} isChecked={checked.activator} onChange={() => handleCheckboxChange("activator")}/> 
        </div>
      </div>

    </div>
  );
}

export default NewSectionModalLvl1


